import { useQueryClient } from '@tanstack/react-query';
import { Link, usePathname } from 'expo-router';
import { Bell, Check, ChevronDown, ChevronRight, Search, X } from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Platform,
  Pressable,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import { useAuth } from '../../contexts/AuthContext';
import { useLayoutChrome } from '../../contexts/LayoutChromeContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { NotificationFeedSheet } from '../../features/notifications/components/NotificationFeedSheet';
import { useUnreadCount } from '../../features/notifications/hooks/useNotifications';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useResponsive } from '../../hooks/useResponsive';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { Box, HStack, Text, VStack } from '../primitives';
import { SearchInput } from '../primitives/SearchInput';
import { buildBreadcrumbs, type RouteOverride, resolvePageTitle } from './breadcrumbs';

function useRouteOverrides(): Record<string, RouteOverride> {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      lead: { label: t('layout.nav.leads'), href: '/leads' },
    }),
    [t],
  );
}

function PageTitle() {
  const pathname = usePathname();
  const { t } = useTranslation();
  const overrides = useRouteOverrides();

  const currentPage = resolvePageTitle(pathname || '/', overrides, t);

  return (
    <Text variant="body" color={colors.text.primary} style={styles.pageTitle} numberOfLines={1}>
      {currentPage}
    </Text>
  );
}

function Breadcrumbs() {
  const pathname = usePathname();
  const { t } = useTranslation();
  const overrides = useRouteOverrides();

  const breadcrumbs = buildBreadcrumbs(pathname || '/', overrides, t);

  return (
    <HStack spacing="xs" align="center" style={styles.breadcrumbsContainer}>
      {breadcrumbs.map((crumb, index) => (
        <HStack key={crumb.href} spacing="xs" align="center">
          {index > 0 && <ChevronRight color={colors.text.disabled} size={14} strokeWidth={1.7} />}
          {crumb.isLast ? (
            <Text variant="bodySmall" color={colors.text.primary} style={styles.breadcrumbActive}>
              {crumb.label}
            </Text>
          ) : (
            <Link href={crumb.href as never} asChild>
              <Pressable>
                <Text
                  variant="bodySmall"
                  color={colors.text.tertiary}
                  style={styles.breadcrumbLink}
                >
                  {crumb.label}
                </Text>
              </Pressable>
            </Link>
          )}
        </HStack>
      ))}
    </HStack>
  );
}

export function NotificationBell({ onPress }: Readonly<{ onPress: () => void }>) {
  const { currentOrganization } = useAuth();
  const { data } = useUnreadCount(currentOrganization?.id);
  const count = data?.count ?? 0;
  const displayCount = count > 99 ? '99+' : String(count);

  return (
    <Pressable
      testID="topbar_notifications_button"
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel="Notifications"
    >
      <View style={styles.iconPill}>
        <Bell color={colors.text.secondary} size={16} strokeWidth={1.8} />
        {count > 0 && (
          <View style={[styles.badge, count > 99 && styles.badgeWide]}>
            <Text variant="caption" color={colors.white} style={styles.badgeText}>
              {displayCount}
            </Text>
          </View>
        )}
      </View>
    </Pressable>
  );
}

export function WorkspaceAvatar({ showDetails = true }: Readonly<{ showDetails?: boolean }>) {
  const { currentOrganization, backendUser, auth0User, setCurrentOrganization } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const reducedMotion = useReducedMotion();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // Keep the menu mounted through the close animation so it can ease out.
  const [menuMounted, setMenuMounted] = useState(false);
  const menuAnim = useRef(new Animated.Value(0)).current;

  const organizations = backendUser?.organizations ?? [];
  const hasMultipleOrgs = organizations.length > 1;

  useEffect(() => {
    if (dropdownOpen && hasMultipleOrgs) {
      setMenuMounted(true);
      Animated.timing(menuAnim, {
        toValue: 1,
        duration: reducedMotion ? 0 : 160,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
    } else if (menuMounted) {
      Animated.timing(menuAnim, {
        toValue: 0,
        duration: reducedMotion ? 0 : 120,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (finished) setMenuMounted(false);
      });
    }
  }, [dropdownOpen, hasMultipleOrgs, menuMounted, reducedMotion, menuAnim]);

  const menuScale = menuAnim.interpolate({ inputRange: [0, 1], outputRange: [0.96, 1] });
  const menuTranslateY = menuAnim.interpolate({ inputRange: [0, 1], outputRange: [-6, 0] });

  const workspaceName =
    currentOrganization?.name ||
    backendUser?.fullName?.split(' ')[0] ||
    auth0User?.name?.split(' ')[0] ||
    auth0User?.email?.split('@')[0] ||
    'FlowsellAI';
  const workspaceInitial = workspaceName[0]?.toUpperCase() || 'F';

  const handleOrgSwitch = (org: {
    organization: { id: string; name: string; createdAt?: string };
    role: string;
  }) => {
    setCurrentOrganization({
      id: org.organization.id,
      name: org.organization.name,
      createdAt: (org.organization as any).createdAt || new Date().toISOString(),
    });
    queryClient.invalidateQueries();
    setDropdownOpen(false);
  };

  return (
    <View style={styles.orgSwitcherWrapper}>
      <Pressable
        onPress={() => hasMultipleOrgs && setDropdownOpen(!dropdownOpen)}
        accessibilityRole={hasMultipleOrgs ? 'button' : 'none'}
        accessibilityLabel={hasMultipleOrgs ? 'Switch organization' : undefined}
      >
        <HStack spacing="sm" align="center" style={styles.workspaceChip}>
          <Box
            height={34}
            width={34}
            alignItems="center"
            justifyContent="center"
            borderRadius="lg"
            backgroundColor={colors.primary.muted}
          >
            <Text variant="caption" color={colors.primary.neon} style={styles.workspaceInitial}>
              {workspaceInitial}
            </Text>
          </Box>
          {showDetails && (
            <VStack spacing="none" style={styles.workspaceText}>
              <Text
                variant="bodySmall"
                color={colors.text.primary}
                style={styles.workspaceName}
                numberOfLines={1}
              >
                {workspaceName}
              </Text>
              <Text variant="caption" color={colors.text.tertiary}>
                {t('topbar.workspace')}
              </Text>
            </VStack>
          )}
          {hasMultipleOrgs && showDetails && (
            <ChevronDown color={colors.text.secondary} size={14} strokeWidth={1.8} />
          )}
        </HStack>
      </Pressable>

      {menuMounted && hasMultipleOrgs && (
        <>
          <Pressable style={styles.orgDropdownBackdrop} onPress={() => setDropdownOpen(false)} />
          <Animated.View
            style={[
              styles.orgDropdown,
              {
                opacity: menuAnim,
                transform: [{ translateY: menuTranslateY }, { scale: menuScale }],
              },
              Platform.OS === 'web'
                ? ({ transformOrigin: 'top right' } as unknown as ViewStyle)
                : null,
            ]}
          >
            {organizations.map((orgMembership) => {
              const isActive = orgMembership.organization.id === currentOrganization?.id;
              return (
                <Pressable
                  key={orgMembership.organization.id}
                  onPress={() => handleOrgSwitch(orgMembership)}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isActive }}
                  accessibilityLabel={`Switch to ${orgMembership.organization.name}`}
                  style={[styles.orgDropdownItem, isActive && styles.orgDropdownItemActive]}
                >
                  <HStack spacing="sm" align="center">
                    <Box
                      height={28}
                      width={28}
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="md"
                      backgroundColor={isActive ? colors.primary.muted : colors.background.tertiary}
                    >
                      <Text
                        variant="caption"
                        color={isActive ? colors.primary.neon : colors.text.secondary}
                        style={styles.workspaceInitial}
                      >
                        {orgMembership.organization.name[0]?.toUpperCase() || '?'}
                      </Text>
                    </Box>
                    <VStack spacing="none" style={styles.orgDropdownText}>
                      <Text
                        variant="bodySmall"
                        color={colors.text.primary}
                        numberOfLines={1}
                        style={styles.workspaceName}
                      >
                        {orgMembership.organization.name}
                      </Text>
                      <Text variant="caption" color={colors.text.tertiary}>
                        {orgMembership.role}
                      </Text>
                    </VStack>
                    {isActive && <Check color={colors.primary.neon} size={14} strokeWidth={2} />}
                  </HStack>
                </Pressable>
              );
            })}
          </Animated.View>
        </>
      )}
    </View>
  );
}

const SEARCH_BAR_FALLBACK_HEIGHT = 50;

export function MobileSearchOverlay({
  visible,
  onClose,
}: Readonly<{ visible: boolean; onClose: () => void }>) {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  const [searchValue, setSearchValue] = useState('');
  // Keep the bar mounted through the collapse so closing animates too.
  const [mounted, setMounted] = useState(visible);
  const [contentHeight, setContentHeight] = useState(SEARCH_BAR_FALLBACK_HEIGHT);
  const expand = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      setMounted(true);
      Animated.timing(expand, {
        toValue: 1,
        duration: reducedMotion ? 0 : 260,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
    } else if (mounted) {
      Animated.timing(expand, {
        toValue: 0,
        duration: reducedMotion ? 0 : 180,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: false,
      }).start(({ finished }) => {
        if (finished) setMounted(false);
      });
    }
  }, [visible, mounted, reducedMotion, expand]);

  if (!mounted) return null;

  const handleClose = () => {
    setSearchValue('');
    onClose();
  };

  const animatedHeight = expand.interpolate({
    inputRange: [0, 1],
    outputRange: [0, contentHeight],
  });
  const translateY = expand.interpolate({
    inputRange: [0, 1],
    outputRange: [-8, 0],
  });

  return (
    <Animated.View
      style={[styles.mobileSearchOverlay, { height: animatedHeight, opacity: expand }]}
    >
      <Animated.View
        style={[styles.mobileSearchRow, { transform: [{ translateY }] }]}
        onLayout={(e) => {
          const h = e.nativeEvent.layout.height;
          if (h > 0 && h !== contentHeight) setContentHeight(h);
        }}
      >
        <View style={styles.mobileSearchInputWrapper}>
          <SearchInput
            value={searchValue}
            onChangeText={setSearchValue}
            placeholder={t('topbar.searchPlaceholder')}
            onClear={() => setSearchValue('')}
            autoFocus
          />
        </View>
        <Pressable
          onPress={handleClose}
          style={styles.mobileSearchClose}
          accessibilityRole="button"
          accessibilityLabel="Close search"
        >
          <X color={colors.text.secondary} size={18} strokeWidth={1.8} />
        </Pressable>
      </Animated.View>
    </Animated.View>
  );
}

interface TopbarProps {
  /**
   * When true, hides the trailing chrome actions on both mobile and desktop:
   * the search affordance (icon on mobile, input on desktop), language
   * switcher, notification bell, and workspace avatar. Title / breadcrumbs
   * stay visible. Use this on detail / focus screens that have their own
   * in-body header and don't need the global chrome actions.
   */
  actionsHidden?: boolean;
}

export function Topbar({ actionsHidden = false }: Readonly<TopbarProps> = {}) {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResponsive();
  const { openNotificationFeed, notificationFeedAvailable } = useLayoutChrome();
  const [searchValue, setSearchValue] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [feedOpen, setFeedOpen] = useState(false);

  const handleSearchClear = () => {
    setSearchValue('');
  };

  // When the native layout has registered a drawer-based notification feed,
  // route the bell through it so we don't open the Modal-based Sheet
  // (which breaks the layout on iOS/Android).
  const handleOpenFeed = () => {
    if (notificationFeedAvailable) openNotificationFeed();
    else setFeedOpen(true);
  };

  if (isMobile) {
    return (
      <View style={styles.mobileContainer}>
        {mobileSearchOpen ? (
          <MobileSearchOverlay
            visible={mobileSearchOpen}
            onClose={() => setMobileSearchOpen(false)}
          />
        ) : (
          <>
            <View style={styles.mobileLeft}>
              <PageTitle />
            </View>
            {!actionsHidden && (
              <HStack spacing="md" align="center">
                <Pressable
                  onPress={() => setMobileSearchOpen(true)}
                  style={styles.iconPill}
                  accessibilityRole="button"
                  accessibilityLabel="Search"
                >
                  <Search color={colors.text.secondary} size={16} strokeWidth={1.8} />
                </Pressable>
                <NotificationBell onPress={handleOpenFeed} />
                <WorkspaceAvatar showDetails={false} />
              </HStack>
            )}
          </>
        )}
        {!notificationFeedAvailable && (
          <NotificationFeedSheet visible={feedOpen} onClose={() => setFeedOpen(false)} />
        )}
      </View>
    );
  }

  return (
    <View style={[styles.container, isTablet && styles.containerTablet]}>
      <Breadcrumbs />

      {!actionsHidden && (
        <View style={[styles.searchContainer, isTablet && styles.searchContainerTablet]}>
          <SearchInput
            value={searchValue}
            onChangeText={setSearchValue}
            placeholder={t('topbar.searchPlaceholder')}
            onClear={handleSearchClear}
          />
        </View>
      )}

      {!actionsHidden && (
        <HStack spacing="md" align="center">
          {Platform.OS === 'web' && <LanguageSwitcher condensed />}
          <NotificationBell onPress={handleOpenFeed} />
          <WorkspaceAvatar showDetails={!isTablet} />
        </HStack>
      )}
      {!notificationFeedAvailable && (
        <NotificationFeedSheet visible={feedOpen} onClose={() => setFeedOpen(false)} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    height: 72,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing['2xl'],
    gap: tokens.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.subtle,
    backgroundColor: 'transparent',
    zIndex: 100,
    overflow: 'visible' as any,
  },
  containerTablet: {
    paddingHorizontal: tokens.spacing.lg,
    gap: tokens.spacing.md,
  },
  mobileContainer: {
    height: 64,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 66,
    paddingRight: tokens.spacing.lg,
    gap: tokens.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.subtle,
    backgroundColor: 'transparent',
  },
  mobileLeft: {
    flex: 1,
    minWidth: 0,
  },
  pageTitle: {
    fontWeight: tokens.fontWeight.semibold,
    letterSpacing: tokens.letterSpacing.tight,
  },
  breadcrumbsContainer: {
    flexShrink: 1,
    minWidth: 0,
  },
  breadcrumbLink: {
    fontWeight: tokens.fontWeight.normal,
  },
  breadcrumbActive: {
    fontWeight: tokens.fontWeight.semibold,
  },
  searchContainer: {
    flex: 1,
    maxWidth: 420,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchContainerTablet: {
    maxWidth: 300,
  },
  iconPill: {
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    padding: tokens.spacing.sm,
    position: 'relative' as const,
    backgroundColor: colors.surface.navChrome,
  },
  badge: {
    position: 'absolute' as const,
    top: -6,
    right: -6,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colors.status.error.main,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  badgeWide: {
    width: 'auto' as unknown as number,
    minWidth: 16,
    paddingHorizontal: 3,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: tokens.fontWeight.bold,
    lineHeight: 12,
    textAlign: 'center' as const,
  },
  workspaceChip: {
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.navChrome,
    paddingVertical: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.sm,
  },
  workspaceInitial: {
    fontWeight: tokens.fontWeight.semibold,
  },
  workspaceText: {
    maxWidth: 140,
  },
  workspaceName: {
    fontWeight: tokens.fontWeight.semibold,
  },
  mobileSearchOverlay: {
    overflow: 'hidden',
  },
  mobileSearchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  mobileSearchInputWrapper: {
    flex: 1,
  },
  mobileSearchClose: {
    padding: tokens.spacing.sm,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.navChrome,
  },
  orgSwitcherWrapper: {
    position: 'relative' as const,
    zIndex: 1000,
  },
  orgDropdownBackdrop: {
    ...StyleSheet.absoluteFillObject,
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 998,
  },
  orgDropdown: {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    marginTop: tokens.spacing.xs,
    minWidth: 240,
    backgroundColor: colors.background.card,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    padding: tokens.spacing.xs,
    zIndex: 999,
    ...(Platform.OS === 'web' ? { boxShadow: '0 8px 32px rgba(0,0,0,0.4)' } : { elevation: 8 }),
  },
  orgDropdownItem: {
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
  },
  orgDropdownItemActive: {
    backgroundColor: colors.primary.muted,
  },
  orgDropdownText: {
    flex: 1,
    minWidth: 0,
  },
});
